import { supabase } from '@/lib/supabase/client';
import { createNotFoundError, fromPostgrestError, normalizeError } from '@/lib/services/errors';
import type { Database } from '@/lib/supabase/database.types';
import type { PostgrestError, PostgrestSingleResponse } from '@supabase/postgrest-js';

type SubscribersTable = Database['public']['Tables']['availability_alert_subscribers'];
export type AvailabilityAlertSubscriberRow = SubscribersTable['Row'];

type SubscriberInsert = SubscribersTable['Insert'];
type SubscriberUpdate = SubscribersTable['Update'];

type EventsTable = Database['public']['Tables']['unit_availability_alert_events'];
export type UnitAvailabilityAlertEventRow = EventsTable['Row'];

type OutboxTable = Database['public']['Tables']['availability_alert_email_outbox'];
export type AvailabilityAlertOutboxRow = OutboxTable['Row'];

export async function subscribeToAvailabilityAlerts(
  email: string,
): Promise<AvailabilityAlertSubscriberRow> {
  try {
    const response = (await supabase
      .rpc('subscribe_to_availability_alerts', { p_email: email })
      .single()
      .throwOnError()) as PostgrestSingleResponse<AvailabilityAlertSubscriberRow>;

    if (response.data === null) {
      throw createNotFoundError('Unable to subscribe to availability alerts.');
    }

    return response.data;
  } catch (error) {
    handleSupabaseError(error);
  }
}

export async function listAvailabilityAlertSubscribers(
  args: {
    signal?: AbortSignal;
  } = {},
): Promise<AvailabilityAlertSubscriberRow[]> {
  try {
    const query = supabase
      .from('availability_alert_subscribers')
      .select('*')
      .order('email', { ascending: true });

    if (args.signal) {
      query.abortSignal(args.signal);
    }

    const response = await query.throwOnError();
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    handleSupabaseError(error);
  }
}

export async function addAvailabilityAlertSubscriber(args: {
  email: string;
  actorId: string;
}): Promise<AvailabilityAlertSubscriberRow> {
  const normalizedEmail = normalizeEmail(args.email);

  try {
    const insertPayload: SubscriberInsert = {
      email: normalizedEmail,
      is_active: true,
      source: 'dashboard',
      created_by: args.actorId,
      removed_at: null,
      removed_by: null,
    };

    const insertResponse = (await supabase
      .from('availability_alert_subscribers')
      .insert(insertPayload)
      .select('*')
      .single()
      .throwOnError()) as PostgrestSingleResponse<AvailabilityAlertSubscriberRow>;

    if (insertResponse.data === null) {
      throw createNotFoundError('Unable to add subscriber.');
    }

    return insertResponse.data;
  } catch (error) {
    if (isPostgrestError(error) && error.code === '23505') {
      return await reactivateAvailabilityAlertSubscriber({
        subscriberEmail: normalizedEmail,
        actorId: args.actorId,
      });
    }

    handleSupabaseError(error);
  }
}

export async function deactivateAvailabilityAlertSubscriber(args: {
  subscriberId: string;
  actorId: string;
}): Promise<AvailabilityAlertSubscriberRow> {
  try {
    const payload: SubscriberUpdate = {
      is_active: false,
      removed_at: new Date().toISOString(),
      removed_by: args.actorId,
    };

    const response = (await supabase
      .from('availability_alert_subscribers')
      .update(payload)
      .eq('id', args.subscriberId)
      .select('*')
      .single()
      .throwOnError()) as PostgrestSingleResponse<AvailabilityAlertSubscriberRow>;

    if (response.data === null) {
      throw createNotFoundError('Subscriber not found.');
    }

    return response.data;
  } catch (error) {
    handleSupabaseError(error);
  }
}

export async function reactivateAvailabilityAlertSubscriber(args: {
  subscriberId?: string;
  subscriberEmail?: string;
  actorId: string;
}): Promise<AvailabilityAlertSubscriberRow> {
  const subscriberId =
    typeof args.subscriberId === 'string' && args.subscriberId.trim().length > 0
      ? args.subscriberId.trim()
      : null;
  const subscriberEmail =
    typeof args.subscriberEmail === 'string' && args.subscriberEmail.trim().length > 0
      ? normalizeEmail(args.subscriberEmail)
      : null;

  if (subscriberId === null && subscriberEmail === null) {
    throw new Error(
      'reactivateAvailabilityAlertSubscriber requires subscriberId or subscriberEmail.',
    );
  }

  try {
    const payload: SubscriberUpdate = {
      is_active: true,
      removed_at: null,
      removed_by: null,
    };

    let query = supabase.from('availability_alert_subscribers').update(payload).select('*');

    if (subscriberId !== null) {
      query = query.eq('id', subscriberId);
    }

    if (subscriberEmail !== null) {
      query = query.eq('email', subscriberEmail);
    }

    const response = (await query
      .single()
      .throwOnError()) as PostgrestSingleResponse<AvailabilityAlertSubscriberRow>;

    if (response.data === null) {
      throw createNotFoundError('Subscriber not found.');
    }

    return response.data;
  } catch (error) {
    handleSupabaseError(error);
  }
}

export async function listUnitAvailabilityAlertEvents(
  args: {
    limit?: number;
    signal?: AbortSignal;
  } = {},
): Promise<UnitAvailabilityAlertEventRow[]> {
  const limit = typeof args.limit === 'number' && args.limit > 0 ? Math.floor(args.limit) : 25;

  try {
    const query = supabase
      .from('unit_availability_alert_events')
      .select('*')
      .order('recorded_at', { ascending: false })
      .limit(limit);

    if (args.signal) {
      query.abortSignal(args.signal);
    }

    const response = await query.throwOnError();
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    handleSupabaseError(error);
  }
}

export async function countAvailabilityAlertOutbox(args: {
  status: AvailabilityAlertOutboxRow['status'];
  signal?: AbortSignal;
}): Promise<number> {
  try {
    const query = supabase
      .from('availability_alert_email_outbox')
      .select('id', { count: 'exact', head: true })
      .eq('status', args.status);

    if (args.signal) {
      query.abortSignal(args.signal);
    }

    const response = await query.throwOnError();
    return typeof response.count === 'number' ? response.count : 0;
  } catch (error) {
    handleSupabaseError(error);
  }
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function handleSupabaseError(error: unknown): never {
  if (isPostgrestError(error)) {
    throw fromPostgrestError(error);
  }
  throw normalizeError(error);
}

function isPostgrestError(error: unknown): error is PostgrestError {
  return typeof error === 'object' && error !== null && 'code' in error && 'message' in error;
}
